import React, { useEffect, useState } from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import axiosInstance from '../../axiosConfig';
import { useNavigate, useParams } from 'react-router-dom';
import { useGlobalStore } from '../../store/globalStore';
import { useDialogStore } from '../../store/dialogStore';
import doneIcon from '../../assets/images/done.svg';
import pdfIcon from '../../assets/images/PDF_icon.svg';
import { formatDate } from '../../utils/utils';
import infoIcon from '../../assets/images/info.svg';

interface Info {
  location: string;
  league: string;
  class: string;
  matchDay: string;
  wertung: string;
  pdf_file: string;
  pdf_brutto: boolean;
  pdf_single: boolean;
  pdf_netto: boolean;
}

interface Team {
  anzeigename: string;
  team_id: string;
  [key: string]: string;
}

const ResultSubmissionPage: React.FC = () => {
  const [info, setInfo] = useState<Info>({
    location: '',
    league: '',
    class: '',
    matchDay: '',
    wertung: '',
    pdf_file: '',
    pdf_brutto: false,
    pdf_single: false,
    pdf_netto: false,
  });
  const [leagues, setLeagues] = useState<Team[]>([]);
  const [teams, setTeams] = useState<
    { team_id: string; result: string; points: string }[]
  >([]);
  const setLoading = useGlobalStore((state) => state.setLoading);
  const isLoading = useGlobalStore((state) => state.isLoading);
  const openDialog = useDialogStore((state) => state.openDialog);
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const navigate = useNavigate();

  const params = useParams();
  const [file, setFile] = useState<File | null>(null);

  const getData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('league/results', {
        params: {
          leagueId: params.leagueId,
          classId: params.classId,
          teamId: params.teamId,
          registrationId: params.registrationId,
          day: params.day,
        },
      });
      setLeagues(response.data.leagues);
      setInfo({
        location: response.data.location,
        league: response.data.title,
        class: response.data.classes_title,
        matchDay: response.data.team_datum,
        wertung: response.data.classes_wertung,
        pdf_file: response.data.pdf_file,
        pdf_brutto: response.data.pdf_brutto,
        pdf_single: response.data.pdf_single,
        pdf_netto: response.data.pdf_netto,
      });
      setTeams(
        response.data.leagues.map((team: Team) => ({
          team_id: team.team_id,
          result: team[`erg_sp${params.day}`] ?? '',
          points: team[`points_sp${params.day}`] ?? '',
        })),
      );
    } catch (error) {
      console.error('Error logging in:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  type WertungType = {
    title: string;
    firstInput: string;
    secondInput: string;
  };

  const getWertungText = (wertung: string): WertungType | undefined => {
    const wertungMap: Record<string, WertungType> = {
      Z: {
        title: 'Schläge über Par',
        firstInput: 'Ergebnis eingeben',
        secondInput: '',
      },
      S: {
        title: 'Summe Stablefordpunkte',
        firstInput: 'Ergebnis eingeben',
        secondInput: '',
      },
      P: {
        title: 'Schläge über CR',
        firstInput: 'Ergebnis eingeben',
        secondInput: 'Ligapunkte',
      },
      C: {
        title: 'Schläge über CR',
        firstInput: 'Ergebnis eingeben',
        secondInput: '',
      },
      D: {
        title: 'Schläge über Par',
        firstInput: 'Ergebnis eingeben',
        secondInput: 'Ligapunkte',
      },
      J: {
        title: 'Stableford (Punkte)',
        firstInput: 'Ergebnis eingeben',
        secondInput: 'Ligapunkte',
      },
    };

    return wertungMap[wertung];
  };

  const handleInputChange = (
    index: number,
    field: 'result' | 'points',
    value: string,
  ) => {
    setTeams((prevTeams) => {
      const newTeams = [...prevTeams];
      newTeams[index][field] = value;
      return newTeams;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('scoring', info.wertung);
      formData.append('leagueId', params.leagueId || '');
      formData.append('classId', params.classId || '');
      formData.append('teamId', params.teamId || '');
      formData.append('registrationId', params.registrationId || '');
      formData.append('day', params.day || '');
      formData.append('teams', JSON.stringify(teams));
      if (file) {
        const fileScope = info.pdf_brutto
          ? 'BRUTTO_L'
          : info.pdf_netto
            ? 'NETTO_L'
            : info.pdf_single
              ? 'SINGLE_L'
              : '';
        if (fileScope) {
          formData.append('file_scope', fileScope);
        }
        formData.append('file', file);
      }
      await axiosInstance.post('league/results/update', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      console.error('Error logging in:', error);
    } finally {
      await getData();
      openDialog(
        <div className="text-md my-10 text-center">
          <img src={doneIcon} alt="" className="mx-auto mb-10" />
          <div className="text-2xl font-medium">
            Die Ergebnisse wurden erfolgreich gespeichert.
          </div>
          <div>
            Bis zur Freigabe durch den Verband erscheint vor der jeweiligen
          </div>
          <div>
            Zahl ein Sternchen <span className="text-red-600">(*)</span>
          </div>
          <div>Vielen Dank für Ihre Unterstützung.</div>
        </div>,
        () => {
          navigate('/league?ref=resultsubmission');
          closeDialog();
        },
      );
      setLoading(false);
    }
  };

  return (
    <div className={`grid gap-4 mx-5`}>
      <div className="grid cursor-default bg-white overflow-hidden gap-1 border p-4 rounded-md relative leading-6">
        <img
          src={infoIcon}
          alt=""
          className="absolute md:inline-block hidden duration-300 right-4 -top-10"
        />

        <div className="font-medium text-lg mb-4">Liga-Ergebnismeldung</div>

        <div className="flex gap-4">
          <div className="w-36 font-medium">📍 Spielort</div>
          <div>{info.location}</div>
        </div>

        <div className="flex gap-4">
          <div className="w-36 font-medium">🏆 Liga</div>
          <div>{info.league}</div>
        </div>

        <div className="flex gap-4">
          <div className="w-36 font-medium">🏅 Klasse</div>
          <div>{info.class}</div>
        </div>

        <div className="flex gap-4">
          <div className="w-36 font-medium">📅 Spieltag</div>
          <div>{formatDate(info.matchDay)}</div>
        </div>
      </div>
      <div className="border flex gap-4 leading-6 rounded-md bg-yellow-50 p-2 border-yellow-300">
        <div className="text-xl flex items-center justify-center rounded-full bg-yellow-300 w-7 h-7">
          !
        </div>
        <div>
          <b>Bitte tragen Sie in die Felder das finale Summenergebnis 🏌️‍♂️</b>
          <br />
          (ohne Streichergebnisse) des Spieltages pro Mannschaft ein.
        </div>
      </div>

      <div className="text-lg font-medium">
        {getWertungText(info.wertung)?.title}
      </div>

      <div className="grid bg-white overflow-hidden gap-1 border p-4 rounded-md relative leading-6">
        {leagues.map((team, index) => (
          <div
            key={index}
            className={`px-4 py-2 ${index % 2 === 0 ? 'bg-gray-50 rounded-md' : ''}`}
          >
            <div className="flex flex-wrap sm:gap-4 items-center">
              <div className="md:w-52 flex-1 md:text-sm text-md w-full font-medium">
                <div>{team.anzeigename}</div>
                <small className="text-blue-800">#{team.team_id}</small>
              </div>
              {['P', 'J', 'D'].includes(info.wertung) && (
                <div className="flex gap-4">
                  <div>
                    <Label className="text-xs text-neutral-500">
                      {getWertungText(info.wertung)?.firstInput}
                    </Label>
                    <Input
                      value={teams[index]?.result ?? ''}
                      placeholder=""
                      name={`result${index + 1}`}
                      disabled={isLoading}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/[a-zA-Z\s]/g, '')
                          .replace(/,/g, '.');
                        handleInputChange(index, 'result', value);
                      }}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-500">
                      {getWertungText(info.wertung)?.secondInput}
                    </Label>
                    <Input
                      value={teams[index]?.points ?? ''}
                      placeholder=""
                      name={`points${index + 1}`}
                      disabled={isLoading}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/[a-zA-Z\s]/g, '')
                          .replace(/,/g, '.');
                        handleInputChange(index, 'points', value);
                      }}
                    />
                  </div>
                </div>
              )}

              {['S', 'C', 'Z'].includes(info.wertung) && (
                <div className=" gap-4">
                  <Label className="text-xs text-neutral-500">
                    {getWertungText(info.wertung)?.firstInput}
                  </Label>
                  <Input
                    name={`result${index + 1}`}
                    value={teams[index]?.result ?? ''}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[a-zA-Z\s]/g, '')
                        .replace(/,/g, '.');
                      handleInputChange(index, 'result', value);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div
        onClick={() => {}}
        className="grid bg-white overflow-hidden gap-1 border p-4 rounded-md relative leading-6"
      >
        <div className="md:flex grid flex-wrap gap-4">
          <img src={pdfIcon} alt="" className="w-10" />
          <div>
            <div className=" font-medium">
              {info.pdf_brutto && 'Teamergebnisse brutto'}
              {info.pdf_netto && 'Teamergebnisse netto'}
              {info.pdf_single && 'Einzelergebnisse netto'}{' '}
              <b className="text-red-600">
                {getWertungText(info.wertung)?.title}
              </b>
            </div>
            <div>Nur PDF bis maximal 5 MB</div>

            {info.pdf_file && (
              <a
                href={info.pdf_file}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-blue-700"
              >
                Hochgeladene Ergebnisliste anzeigen (pdf)
              </a>
            )}
          </div>
          <div className="flex-1">
            <input type="file" accept=".pdf" onChange={handleFileChange} />
          </div>
        </div>
      </div>
      <Button className="w-52" disabled={isLoading} onClick={handleSubmit}>
        {isLoading ? 'Bitte warten...' : '  Diese Ergebnisse melden'}
      </Button>
    </div>
  );
};

export default ResultSubmissionPage;
